import WindowSize from "../services/WindowSize.js";
import CustomCursor from "./CustomCursor";
import Marquee from "./Marquee.js";

// Mathematical constants for the trail vector direction (136 degrees angle)
const TRAIL_ANGLE = (136 * Math.PI) / 180;
const TRAIL_DIR_X = Math.cos(TRAIL_ANGLE);
const TRAIL_DIR_Y = -Math.sin(TRAIL_ANGLE);

/**
 * Handles page interactions
 *
 * @package AGDS
 * @category Modules
 * @module PageInteractions
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class PageInteractions {
    /**
     * @param [options={}]
     */
    constructor(options = {}) {
        this.resizeUnsubscribe = null;
    }

    /**
     * Initializes.
     *
     * @returns {void}
     */
    init() {
        // Initialize custom cursor on startup
        this.customCursor = new CustomCursor();

        // Initialize marquee on startup
        this.marquee = Marquee.initAll();

        // Apply blur effect to the body element
        this.initApplyBlur();

        // Number the section
        this.initSectionNum();
    }

    /**
     * Applies blur effect to the body element.
     *
     * @returns {void}
     */
    initApplyBlur() {
        // Apply blur effect immediately and once custom fonts are fully loaded
        if (document.fonts?.ready) {
            document.fonts.ready.then(() => this.applyBlur());
        }
        this.applyBlur();

        // Subscribe to window size changes using the WindowSize service (with built-in debounce)
        this.resizeUnsubscribe = WindowSize.onChange(() => {
            this.applyBlur();
        });
    }

    /**
     * Computes and applies dynamic text blur/trail effects
     * based on element font size and the [data-blur] attribute.
     *
     * @returns {void}
     */
    applyBlur() {
        const blurElements = document.querySelectorAll("[data-blur]");

        blurElements.forEach((el) => {
            const computedStyle = window.getComputedStyle(el);
            const fontSize = parseFloat(computedStyle.fontSize);
            const blurMultiplier = parseFloat(el.dataset.blur) || 0;
            const blurLength = fontSize * blurMultiplier;

            if (blurLength > 0.5) {
                // Extract RGB color components safely from computed style
                const colorMatch = computedStyle.color.match(/\d+/g);
                const rgbColor = colorMatch ? colorMatch.slice(0, 3).join(",") : "0, 0, 0";

                const trailValue = this.#trail(blurLength, rgbColor);
                el.style.setProperty("--blur", trailValue);
            } else {
                el.style.setProperty("--blur", "none");
            }
        });
    }

    /**
     * Numbers the sections on the page by updating elements with [data-section-count] attributes.
     *
     * @returns {void}
     */
    initSectionNum() {
        const secTitles = document.querySelectorAll("[data-section-count]");
        if (secTitles.length < 0) return;

        secTitles.forEach((item, index) => {
            const numEl = item.querySelector(".num");
            if (numEl) {
                numEl.textContent = "(" + (index + 1).toString().padStart(2, "0") + ")";
            }
        });
    }

    /**
     * Generates a multi-layered shadow/trail string for the blur effect.
     *
     * @private
     * @param {number} length - The calculated trail length based on font size and modifier.
     * @param {string} rgb - The RGB color string (e.g., "255, 255, 255").
     * @returns {string} The CSS shadow layers string joined by commas.
     */
    #trail(length, rgb) {
        const steps = Math.max(5, Math.min(16, Math.round(length / 7)));
        const shadows = [];
        const soft = (length / steps) * 1.1;

        for (let i = 0; i < steps; i++) {
            const t = (i / (steps - 1) - 0.5) * length;
            const alpha = Math.max(0.05, 0.42 * (1 - Math.abs(t) / (length * 0.62)));

            shadows.push(
                `${(t * TRAIL_DIR_X).toFixed(1)}px ${(t * TRAIL_DIR_Y).toFixed(1)}px ${soft.toFixed(1)}px rgba(${rgb},${alpha.toFixed(2)})`,
            );
        }

        return shadows.join(",");
    }

    /**
     * Cleans up event subscriptions when the instance is destroyed.
     *
     * @returns {void}
     */
    destroy() {
        if (typeof this.resizeUnsubscribe === "function") {
            this.resizeUnsubscribe();
        }
    }
}
